import { Button, Modal } from 'antd';
import { useLogoutMutation } from 'api/AuthApi';
import { useAcceptHipaaMutation, useGetActiveHipaaVersionQuery } from 'api/HipaaApi';
import SpinComponent from 'components/Tool/SpinComponent/SpinComponent';
import dayjs from 'dayjs';
import { PointerEvent as ReactPointerEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectAuthUser } from 'store/authSlice';
import { closeModal, selectModalName } from 'store/modalSlice';
import { useAppDispatch } from 'store/store';
import { securityLogger } from 'utils/securityLogger';
import { sanitizeHtml } from 'utils/sanitizeHtml';
import './HipaaAcceptModal.css';

export const HIPAA_ACCEPT_MODAL_NAME = 'HIPAA_ACCEPT_MODAL_NAME';

const SIGNATURE_LINE_WIDTH = 2;
const SIGNATURE_PAD_HEIGHT = 180;

const getSignerName = (user: ReturnType<typeof selectAuthUser>): string => {
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();
  return fullName || user?.email || 'Authenticated patient';
};

type Point = {
  x: number;
  y: number;
};

const getCanvasBounds = (canvas: HTMLCanvasElement) => {
  const bounds = canvas.getBoundingClientRect?.();

  return {
    left: bounds?.left || 0,
    top: bounds?.top || 0,
    width: Math.max(bounds?.width || canvas.clientWidth || canvas.offsetWidth || canvas.width || 1, 1),
    height: Math.max(bounds?.height || canvas.clientHeight || canvas.offsetHeight || canvas.height || SIGNATURE_PAD_HEIGHT, 1)
  };
};

export function HipaaAcceptModal() {
  const dispatch = useAppDispatch();
  const authUser = useSelector(selectAuthUser);
  const { data: hipaaVersion, isLoading: isHipaaLoading } = useGetActiveHipaaVersionQuery();
  const [acceptHipaa, { isLoading: isAccepting }] = useAcceptHipaaMutation();
  const [logout] = useLogoutMutation();

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawingRef = useRef(false);
  const lastPointRef = useRef<Point | null>(null);
  const hasSignatureRef = useRef(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [openedAt] = useState(() => new Date());
  const [signedAt, setSignedAt] = useState<Date | null>(null);
  const sanitizedAuthorizationText = sanitizeHtml(hipaaVersion?.authorizationText || '');
  const signerName = useMemo(() => getSignerName(authUser), [authUser]);
  const displayedSignedAt = signedAt || openedAt;
  const isAuthorizeDisabled = !hasSignature || isAccepting || !hipaaVersion;

  const configureSignatureContext = useCallback((canvas: HTMLCanvasElement) => {
    const context = canvas.getContext('2d');
    if (!context) {
      return null;
    }

    context.lineCap = 'round';
    context.lineJoin = 'round';
    context.strokeStyle = '#1f2937';
    context.fillStyle = '#1f2937';
    context.lineWidth = SIGNATURE_LINE_WIDTH;

    return context;
  }, []);

  const resizeCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const bounds = getCanvasBounds(canvas);
    const width = bounds.width;
    const height = bounds.height;
    const ratio = window.devicePixelRatio || 1;
    const snapshot = hasSignatureRef.current ? canvas.toDataURL('image/png') : null;

    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);

    const context = configureSignatureContext(canvas);
    if (!context) {
      return;
    }

    context.setTransform(1, 0, 0, 1, 0, 0);
    context.scale(ratio, ratio);
    context.clearRect(0, 0, width, height);

    if (snapshot) {
      const image = new Image();
      image.onload = () => {
        context.drawImage(image, 0, 0, width, height);
      };
      image.src = snapshot;
    }
  }, [configureSignatureContext]);

  useEffect(() => {
    resizeCanvas();
  }, [resizeCanvas]);

  useEffect(() => {
    let rafId: number | null = null;

    const handleResize = () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }

      rafId = window.requestAnimationFrame(() => resizeCanvas());
    };

    window.addEventListener('resize', handleResize);

    return () => {
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
      window.removeEventListener('resize', handleResize);
    };
  }, [resizeCanvas]);

  const markSignaturePresent = useCallback(() => {
    if (!hasSignatureRef.current) {
      hasSignatureRef.current = true;
      setHasSignature(true);
    }

    setSignedAt((currentValue) => currentValue || new Date());
  }, []);

  const getPointFromEvent = (event: ReactPointerEvent<HTMLCanvasElement>): Point | null => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return null;
    }

    const bounds = getCanvasBounds(canvas);
    return {
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top
    };
  };

  const drawPoint = useCallback(
    (point: Point) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const context = configureSignatureContext(canvas);
      if (!context) {
        return;
      }

      context.beginPath();
      context.arc(point.x, point.y, SIGNATURE_LINE_WIDTH / 1.5, 0, Math.PI * 2);
      context.fill();
    },
    [configureSignatureContext]
  );

  const drawSegment = useCallback(
    (start: Point, end: Point) => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      const context = configureSignatureContext(canvas);
      if (!context) {
        return;
      }

      context.beginPath();
      context.moveTo(start.x, start.y);
      context.lineTo(end.x, end.y);
      context.stroke();
    },
    [configureSignatureContext]
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    event.preventDefault();
    const point = getPointFromEvent(event);
    if (!point) {
      return;
    }

    drawingRef.current = true;
    lastPointRef.current = point;
    drawPoint(point);
    markSignaturePresent();
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (!drawingRef.current) {
      return;
    }

    event.preventDefault();
    const point = getPointFromEvent(event);
    if (!point || !lastPointRef.current) {
      return;
    }

    drawSegment(lastPointRef.current, point);
    lastPointRef.current = point;
    markSignaturePresent();
  };

  const stopDrawing = () => {
    drawingRef.current = false;
    lastPointRef.current = null;
  };

  const clearSignature = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }

    const context = configureSignatureContext(canvas);
    if (!context) {
      return;
    }

    context.clearRect(0, 0, canvas.width, canvas.height);
    hasSignatureRef.current = false;
    setHasSignature(false);
    setSignedAt(null);
    stopDrawing();
    resizeCanvas();
  }, [configureSignatureContext, resizeCanvas]);

  const handleAccept = async () => {
    if (!hipaaVersion || !hasSignature || !canvasRef.current) {
      return;
    }

    try {
      const signature = canvasRef.current.toDataURL('image/png');
      await acceptHipaa({
        authorizationVersion: hipaaVersion.version,
        authorizationTitle: hipaaVersion.title,
        authorizationCreatedAt: hipaaVersion.createdAt,
        signerName,
        signedByUserId: authUser?.userId || authUser?.id,
        signedByPatientProfileId: authUser?.patientProfileId || undefined,
        clientSignedAt: displayedSignedAt.toISOString(),
        clientTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userAgent: window.navigator.userAgent,
        signature
      }).unwrap();
      dispatch(closeModal());
    } catch (e: any) {
      securityLogger.error('Failed to accept HIPAA authorization', e);
    }
  };

  const handleLogout = async () => {
    try {
      await logout().unwrap();
      window.location.reload();
    } catch (e: any) {
      securityLogger.error('Failed to logout after HIPAA decline', e);
    }
  };

  return (
    <Modal
      open={true}
      footer={null}
      closable={false}
      maskClosable={false}
      title={
        <div className="hipaa-modal-title">
          {hipaaVersion?.title || 'Authorization for Storage and Delivery of Messages'}
        </div>
      }
      width={600}
      centered
      className="hipaa-accept-modal"
    >
      <SpinComponent isLoading={isHipaaLoading}>
        <div className="hipaa-modal-container">
          <div className="hipaa-content" dangerouslySetInnerHTML={{ __html: sanitizedAuthorizationText }} />

          <div className="hipaa-signature-card">
            <div className="hipaa-signature-header">
              <div>
                <div className="hipaa-signature-label">Signature</div>
                <div className="hipaa-signature-hint">Draw your electronic signature to authorize this HIPAA release.</div>
              </div>
              <Button type="default" onClick={clearSignature} disabled={!hasSignature}>
                Clear Signature
              </Button>
            </div>

            <canvas
              ref={canvasRef}
              className="hipaa-signature-canvas"
              height={SIGNATURE_PAD_HEIGHT}
              data-testid="hipaa-signature-canvas"
              aria-label="HIPAA signature pad"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={stopDrawing}
              onPointerLeave={stopDrawing}
              onPointerCancel={stopDrawing}
            />

            <div className="hipaa-signature-meta">
              <div>
                <span className="hipaa-signature-meta-label">Signed by:</span> {signerName}
              </div>
              <div>
                <span className="hipaa-signature-meta-label">Date/Time:</span>{' '}
                {hasSignature ? dayjs(displayedSignedAt).format('MMMM D, YYYY h:mm A') : 'Not signed yet'}
              </div>
            </div>

            <div className="hipaa-signature-footnote">The final signed timestamp is confirmed by the server when you authorize.</div>
          </div>

          <div className="hipaa-action-buttons">
            <Button
              type="primary"
              size="large"
              block
              loading={isAccepting}
              disabled={isAuthorizeDisabled}
              onClick={handleAccept}
            >
              Authorize
            </Button>
            <Button type="link" onClick={handleLogout} danger>
              I do not agree — Log out
            </Button>
          </div>
        </div>
      </SpinComponent>
    </Modal>
  );
}

function HipaaAcceptModalWrapper() {
  const modalName = useSelector(selectModalName);

  return modalName === HIPAA_ACCEPT_MODAL_NAME ? <HipaaAcceptModal /> : null;
}

export default HipaaAcceptModalWrapper;
